import { ChangeDetectionStrategy, Component, Inject, Injector, OnInit } from '@angular/core';
import { ISizeName } from '@df/core/service/client';
import { MultisiteService } from '@df/core/service/multisite';
import { UiComponent } from '@df/ui/ui.component';
import { DfModal } from '@df/ui/modal/df-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { MultisiteRedirectService } from '@df/ui/multisite/multisite-redirect.service';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { NModalLayoutMiddle } from '../../common/modal/layout-middle/modal-layout-middle.component';
import { storeOptionsFn } from '../switch-modal/multisite-switch-modal.component';
import { NMultisiteSwitchModal } from '../switch-modal/multisite-switch-modal.interfaces';
import { MultisiteGeolocationInitiatorService } from './multisite-geolocation-initiator.service';
import { NMultisiteGeolocationModal } from './multisite-geolocation-modal.interfaces';

@DfModal({
    id: NMultisiteGeolocationModal.componentId,
    position: 'custom',
    config: (sizeName: ISizeName) => ({
        ...NModalLayoutMiddle.modalConfig,
        width: NModalLayoutMiddle.getWidth(sizeName)
    }),
    blockGroup: 'modals',
    priority: 30,
    initiators: [MultisiteGeolocationInitiatorService],
    sizeRestriction: sizeId => ['S', 'M', 'L', 'X'],
    reopenPolicy: NDfModal.ReopenPolicy.first
})
@ICmsComponent<MultisiteGeolocationModalComponent>({
    name: 'Multisite Geolocation Popup',
    description: 'Appears when user visits the store from a country that matches another one. Offers user to change store.',
    id: NMultisiteGeolocationModal.componentId,
    block: {
        url: NMultisiteGeolocationModal.componentId,
        scope: 'website'
    },
    onDemandOnly: true,
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'testing',
            title: 'CMS testing setup'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'cmsCountryCode',
            title: 'Force country code',
            description: 'Put any country code to preview popup in that variant (CMS only)',
            required: false
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'variants',
            title: 'Variant',
            description: 'First variant that matches user country code is used for display.',
            itemName: 'Variant',
            required: true,
            defaultSize: 3,
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'countries',
                    title: 'Countries',
                    description: 'Coma separated list of 2-char country-codes, see https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2',
                    required: true
                },
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'defaultSiteId',
                    title: 'Default site',
                    description: 'Default store for given country codes',
                    options: storeOptionsFn,
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Image',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Title',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'subtitle1',
                    title: 'Subtitle 1',
                    required: false
                },
                {
                    type: NICmsContentField.EFieldType.textarea,
                    key: 'subtitle2',
                    title: 'Subtitle 2',
                    required: false
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'confirmButtonCopy',
                    title: 'Go to default location button copy',
                    default: 'Update location',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'closeButtonCopy',
                    title: 'Remain on website button copy',
                    default: 'Continue',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'viewAllCopy',
                    title: 'View other countries button copy',
                    default: 'View other countries',
                    required: true
                }
            ]
        }
    ]
})
@Component({
    selector: 'multisite-geolocation-modal',
    templateUrl: './multisite-geolocation-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultisiteGeolocationModalComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NMultisiteGeolocationModal.IBlockDataContent;

    constructor(
        private multisiteRedirectService: MultisiteRedirectService,
        private dfModalService: DfModalService,
        private multisiteService: MultisiteService,
        private modalRef: ModalRef<any>,
        @Inject(MODAL_DATA) private data: NMultisiteGeolocationModal.IInitiatorData
    ) {
        super();
    }

    /**
     * Retrieves a variant matching currently previewed content
     */
    protected get contentVariant(): NMultisiteGeolocationModal.IBlockContentVariant | undefined {
        const variants = this.content?.variants;
        if (!variants) {
            return undefined;
        }
        const code = ((this.client.isCms && this.content?.cmsCountryCode) || this.data.matchingCountry || '').toUpperCase();
        return (
            (code &&
                variants.find(variant =>
                    variant.countries
                        ?.toUpperCase()
                        .split(',')
                        .find(countryCode => countryCode.trim() === code)
                )) ||
            variants[0]
        );
    }

    /**
     * Confirm location change
     */
    protected confirm(): void {
        if (this.contentVariant) {
            this.goTo(this.contentVariant.defaultSiteId);
        }
    }

    /**
     * View list of all stores
     */
    protected viewAll() {
        this.close();

        this.dfModalService.open(undefined, NMultisiteSwitchModal.componentId);
    }
    /**
     * Closes the modal
     */
    protected close() {
        this.modalRef.close();
    }

    /**
     * Stay on current website
     */
    protected stay() {
        this.goTo(this.multisiteService.current.siteId);
    }

    /**
     * Changes location using multisite service
     * what keeps current path (or uses alternate) and saves website preferrence
     */
    private goTo(siteId: number | string, $event?: Event) {
        if (this.multisiteRedirectService.to(siteId)) {
            $event?.preventDefault();
        } else {
            this.close();
        }
    }
}
